import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// web/src/components/chat/AgentMessage.tsx
// Full agent message component. Renders everything a response can contain:
//   - sub_role badge + agent chips used
//   - confidence bar
//   - markdown content
//   - pipeline steps (if present)
//   - artifact card (if produced a document)
//   - action cards (approval queue)
//   - feedback row (👍👎 + grounded badge + reasoning trace)
import { memo, useState } from "react";
import { BookOpen } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Badge, agentVariant } from "../ui/Badge";
import { ConfidenceBar } from "../ui/ConfidenceBar";
import { AgentChip } from "../ui/AgentChip";
import { agentLabel } from "../../lib/agentConfig";
import { PipelineSteps } from "./PipelineSteps";
import { ArtifactCard } from "./ArtifactCard";
// ActionCard rendered inside template components, not directly here.
import { FeedbackRow } from "./FeedbackRow";
import { useArtifactsStore } from "../../stores/artifactsStore";
import { InteractiveCardHost } from "../interactive-cards/InteractiveCardHost";
import { useInteractiveCardStore } from "../../stores/interactiveCardStore";
import { Renderer } from "../../rendering";
// SpecialistCard rendered inside template components, not directly here.
import { AnalystReport } from "./templates/AnalystReport";
import { NewsBrief } from "./templates/NewsBrief";
import { QuickAnswer } from "./templates/QuickAnswer";
import { DataView } from "./templates/DataView";
import { Conversational } from "./templates/Conversational";
const SUB_ROLE_LABELS = {
    analyst: "Analysis",
    reporter: "Report",
    advisor: "Advice",
    planner: "Plan",
    monitor: "Alert",
    executor: "Action",
};
const MD_COMPONENTS = {
    p: ({ children }) => _jsx("p", { className: "chat-p", children: children }),
    h1: ({ children }) => _jsx("h2", { className: "chat-h1", children: children }),
    h2: ({ children }) => _jsx("h3", { className: "chat-h2", children: children }),
    h3: ({ children }) => _jsx("h4", { className: "chat-h3", children: children }),
    ul: ({ children }) => _jsx("ul", { className: "chat-ul space-y-0.5", children: children }),
    ol: ({ children }) => _jsx("ol", { className: "chat-ol space-y-0.5", children: children }),
    li: ({ children }) => _jsx("li", { className: "chat-li", children: children }),
    strong: ({ children }) => _jsx("strong", { className: "chat-strong", children: children }),
    em: ({ children }) => _jsx("em", { className: "chat-em", children: children }),
    blockquote: ({ children }) => _jsx("blockquote", { className: "chat-blockquote", children: children }),
    hr: () => _jsx("hr", { className: "border-slate-800 my-2" }),
    a: ({ children, href }) => _jsx("a", { href: href, className: "chat-link", children: children }),
    code: ({ children, className }) => {
        const isBlock = className?.includes("language-");
        return isBlock
            ? _jsx("code", { className: "chat-code-block", children: children })
            : _jsx("code", { className: "chat-code-inline", children: children });
    },
    pre: ({ children }) => _jsx("pre", { className: "chat-pre", children: children }),
    table: ({ children }) => (_jsx("div", { className: "overflow-x-auto my-2 rounded-xl border border-slate-700", children: _jsx("table", { className: "w-full", children: children }) })),
    thead: ({ children }) => _jsx("thead", { className: "bg-slate-800/60", children: children }),
    th: ({ children }) => (_jsx("th", { className: "px-4 py-2 text-[10px] font-semibold uppercase tracking-wide text-slate-500 text-left border-b border-slate-700", children: children })),
    tbody: ({ children }) => _jsx("tbody", { children: children }),
    tr: ({ children }) => (_jsx("tr", { className: "border-b border-slate-800/50 hover:bg-slate-800/30 transition-colors last:border-0", children: children })),
    td: ({ children }) => _jsx("td", { className: "chat-td", children: children }),
};
/** Styled markdown renderer for chat content. Exported so template
    components (AnalystReport, NewsBrief, etc.) can render markdown
    with the same paragraph/heading/list classes as the rest of the chat. */
export function AgentMarkdown({ content }) {
    return (_jsx(ReactMarkdown, { remarkPlugins: [remarkGfm], components: MD_COMPONENTS, children: content }));
}
function AgentMessageImpl({ message, sophistication, onApproveCard = () => { }, onRejectCard = () => { }, onAskLunaAboutCard, onCardSubmit, onCardSkip, onSuggestionSelect, }) {
    const getArtifact = useArtifactsStore((s) => s.getArtifact);
    const addArtifact = useArtifactsStore((s) => s.addArtifact);
    const deleteArtifact = useArtifactsStore((s) => s.deleteArtifact);
    const isPinned = useArtifactsStore((s) => s.isPinned);
    const artifacts = useArtifactsStore((s) => s.artifacts);
    const { submitCardResponse } = useInteractiveCardStore();
    function handlePin() {
        if (message.role !== "assistant" || !message.id)
            return;
        if (isPinned(message.id)) {
            const existing = artifacts.find((a) => a.type === "pinned_message" && a.message_id === message.id);
            if (existing)
                void deleteArtifact(existing.id);
            return;
        }
        const title = message.content.slice(0, 60).replace(/\n/g, " ").trim() +
            (message.content.length > 60 ? "…" : "");
        void addArtifact({
            type: "pinned_message",
            title,
            content: message.content,
            agent_id: message.agent_id ?? "luna",
            agents_chain: message.agents_used ?? [message.agent_id ?? "luna"],
            conversation_id: null,
            renders: message.renders,
            message_id: message.id,
        });
    }
    if (message.role === "user") {
        return (_jsx("div", { className: "flex justify-end", children: _jsx("div", { className: "max-w-[75%] bg-violet-600/20 border border-violet-500/20 rounded-2xl rounded-tr-sm px-4 py-2.5", children: _jsx("p", { className: "text-sm text-slate-200 leading-relaxed", children: message.content }) }) }));
    }
    // Assistant message
    const agentId = message.agent_id ?? "luna";
    const variant = agentVariant(agentId);
    const subRoleLabel = message.sub_role ? (SUB_ROLE_LABELS[message.sub_role] ?? message.sub_role) : null;
    const artifact = message.artifact_id ? getArtifact(message.artifact_id) : null;
    const hasPipeline = message.pipeline_steps && message.pipeline_steps.length > 0;
    // Read mode — only relevant for analyst-style responses. Lifted here so the
    // trigger can sit next to the agent name in the meta row while the overlay
    // itself lives inside <AnalystReport/>.
    const template = message.response_template ?? "analyst_report";
    const hasAnalystContent = template === "analyst_report" &&
        !!(message.executive_recommendation ||
            message.executive_summary ||
            message.situation_narrative ||
            (message.unified_recommendations && message.unified_recommendations.length > 0));
    const [readMode, setReadMode] = useState(false);
    // First-time nudge: pulse the Read-mode button until the user opens it
    // once. Tracked in localStorage so the cue stops permanently after the
    // first click. Most users miss the small affordance on long reports —
    // this surfaces it without being intrusive after they've discovered it.
    const [readModeSeen, setReadModeSeen] = useState(() => {
        try {
            return localStorage.getItem("matchrail.read_mode_seen") === "1";
        }
        catch {
            return true; // storage blocked → assume seen, skip the cue
        }
    });
    const openReadMode = () => {
        setReadMode(true);
        if (!readModeSeen) {
            try {
                localStorage.setItem("matchrail.read_mode_seen", "1");
            }
            catch {
                // ignore
            }
            setReadModeSeen(true);
        }
    };
    return (_jsxs("div", { className: "flex gap-3 group", children: [_jsx("div", { className: "mt-1 shrink-0 w-6 flex items-start justify-center", children: _jsx("span", { className: `text-sm select-none ${variant === "luna" ? "text-violet-400" :
                        variant === "ptolemy" ? "text-indigo-400" :
                            variant === "vault" ? "text-teal-400" :
                                variant === "orion" ? "text-blue-400" :
                                    "text-slate-500"}`, children: agentId === "luna" ? "✦" :
                        agentId === "ptolemy" ? "◆" :
                            agentId === "vault" ? "◈" :
                                agentId === "orion" ? "◎" : "◆" }) }), _jsxs("div", { className: "flex-1 min-w-0 space-y-2", children: [_jsxs("div", { className: "flex items-center gap-2 flex-wrap", children: [_jsx("span", { className: "text-xs font-medium text-slate-400", children: agentLabel(agentId) }), hasAnalystContent && (_jsxs("button", { type: "button", onClick: openReadMode, className: `inline-flex items-center gap-1.5 text-xs font-medium text-violet-100 bg-violet-500/20 hover:bg-violet-500/35 active:bg-violet-500/45 border border-violet-400/50 hover:border-violet-300/70 rounded-md px-2.5 py-1 shadow-sm transition-all${readModeSeen ? "" : " animate-pulse ring-2 ring-violet-400/60"}`, title: "Open in Read mode", "aria-label": "Open this report in Read mode", style: { minHeight: "28px" }, children: [_jsx(BookOpen, { className: "h-3.5 w-3.5 shrink-0" }), _jsx("span", { children: "Open Read Mode" })] })), subRoleLabel && _jsx(Badge, { variant: variant, size: "xs", children: subRoleLabel }), message.confidence !== undefined && (_jsx(ConfidenceBar, { value: message.confidence, showLabel: true, className: "w-20", height: "xs" })), message.agents_used && message.agents_used.length > 0 && (_jsx("div", { className: "flex gap-1 flex-wrap", children: message.agents_used.map((a) => (_jsx(AgentChip, { agentId: a }, a))) }))] }), hasPipeline && (_jsx(PipelineSteps, { steps: message.pipeline_steps, isLive: message.pipeline_steps.some((s) => s.status === "running"), etaSeconds: message.eta_seconds, etaN: message.eta_n, className: "py-1" })), (() => {
                        const template = message.response_template ?? "analyst_report";
                        switch (template) {
                            case "news_brief":
                                return _jsx(NewsBrief, { message: message });
                            case "quick_answer":
                                return _jsx(QuickAnswer, { message: message });
                            case "data_view":
                                return _jsx(DataView, { message: message });
                            case "conversational":
                                return _jsx(Conversational, { message: message });
                            case "analyst_report":
                            default:
                                return (_jsx(AnalystReport, { message: message, onApproveCard: onApproveCard, onRejectCard: onRejectCard, onAskLunaAboutCard: onAskLunaAboutCard, readMode: readMode, onCloseReadMode: () => setReadMode(false) }));
                        }
                    })(), (!message.specialists || message.specialists.length === 0) &&
                        message.renders && message.renders.length > 0 && (_jsx("div", { className: "space-y-3 mt-2", children: message.renders.map((block, i) => (_jsx(Renderer, { block: block }, i))) })), artifact && (_jsx(ArtifactCard, { artifactId: artifact.id, type: artifact.type, title: artifact.title, agentChain: artifact.agents_chain, confidence: artifact.confidence, className: "mt-2" })), message.interactive_card && (_jsx(InteractiveCardHost, { card: message.interactive_card, onSubmit: onCardSubmit ?? (async (cardId, cardType, values) => {
                            await submitCardResponse({ cardId, cardType, values });
                        }), onSkip: onCardSkip ?? (() => { }) })), message.suggested_prompts && message.suggested_prompts.length > 0 && onSuggestionSelect && (_jsx("div", { className: "flex flex-wrap gap-1.5 pt-2", children: message.suggested_prompts.map((p, i) => (_jsx("button", { onClick: () => onSuggestionSelect(p), className: "text-[11px] text-violet-400 bg-violet-500/10 hover:bg-violet-500/20 border border-violet-500/20 rounded-full px-3 py-1 transition-colors text-left", children: p }, i))) })), _jsx(FeedbackRow, { traceId: message.reasoning_trace_id, langfuseTraceUrl: message.langfuse_trace_url, knowledgeUsed: message.knowledge_used, sophistication: sophistication, isPinned: message.id ? isPinned(message.id) : false, onPin: message.id ? handlePin : undefined, className: "pt-1" })] })] }));
}
// Memoised so chat-input keystrokes in the parent (AgentsPage) don't
// cascade a full re-render of every message in the thread. Custom
// comparator: only re-render if the message identity / shallow content
// changed, OR if a callback whose identity matters here was swapped.
// Most parent re-renders create new arrow functions for the on*Card
// callbacks; we deliberately ignore those — AgentMessage doesn't depend
// on their identity, and re-rendering on every keystroke just to track
// callback churn is what was producing character-by-character lag in
// the input box (Langfuse-traced regression after the dashboard ship).
export const AgentMessage = memo(AgentMessageImpl, (prev, next) => {
    // Re-render only when the underlying message data changes.
    if (prev.message !== next.message)
        return false;
    if (prev.sophistication !== next.sophistication)
        return false;
    return true;
});
